import {
  GovernanceChangeHistoryProjection,
  ReviewProcessReadinessProjection,
  TenantGovernancePolicyProjection,
} from "@/api/tenantGovernanceApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatReadinessLabel } from "@/utils/semanticLabels";

import { PageHeader, Pill } from "./shared";

export function LaunchReadinessSection({
  projection,
  recentChanges,
  saving,
  staffCount,
  onProvisionReviewProcess,
  showHeader = true,
}: {
  projection: TenantGovernancePolicyProjection | null;
  recentChanges: GovernanceChangeHistoryProjection[];
  saving: boolean;
  staffCount: number;
  onProvisionReviewProcess: (programCode: string, academicYear: string) => void;
  showHeader?: boolean;
}) {
  const readinessRows = projection?.review_process_readiness ?? [];
  const readyReviewProcesses = readinessRows.filter(
    (row) => row.status === "ready_for_launch"
  ).length;
  const selectedReviewProcesses = readinessRows.filter(
    (row) => row.selected_workflow_template_id
  ).length;
  const provisionedReviewProcesses = readinessRows.filter(
    (row) => row.provisioned_workflow_config_id && !row.mismatch
  ).length;
  const hasReviewProcess = readyReviewProcesses > 0;
  const blockers = hasReviewProcess ? 0 : 1;

  return (
    <>
      {showHeader ? (
        <PageHeader
          title="Publication readiness"
          subtitle={projection?.tenant_name ?? "Select an organization"}
        />
      ) : null}
      <div className={showHeader ? "space-y-4 p-4" : "space-y-4"}>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 text-sm text-blue-950">
            <div className="font-semibold">Ownership</div>
            <p className="mt-1">
              PM prepares the scholarship. Admin confirms readiness and activates launch.
            </p>
          </CardContent>
        </Card>

        <div
          className={`rounded-lg border p-5 ${
            blockers === 0
              ? "border-green-200 bg-green-50 text-green-900"
              : "border-red-200 bg-red-50 text-red-900"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-lg font-semibold">
                {blockers === 0
                  ? "READY FOR STUDENT APPLICATIONS"
                  : "NOT READY FOR STUDENT APPLICATIONS"}
              </div>
              <p className="mt-1 text-sm">
                {blockers === 0
                  ? "All required setup is complete. Students can apply now."
                  : "Fix the blockers below before students apply."}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-semibold">{blockers}</div>
              <div className="text-xs">launch blockers</div>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Launch checklist</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <ChecklistItem
              ok={Boolean(projection)}
              title="Application workspace selected"
              detail={projection?.tenant_slug ?? "No organization selected"}
            />
            <ChecklistItem
              ok={selectedReviewProcesses > 0}
              title="Selected review process"
              detail={
                selectedReviewProcesses > 0
                  ? `${selectedReviewProcesses} selected review process`
                  : "PM must choose an approved review process"
              }
            />
            <ChecklistItem
              ok={provisionedReviewProcesses > 0}
              title="Provisioned review process"
              detail={
                provisionedReviewProcesses > 0
                  ? `${provisionedReviewProcesses} provisioned runtime review process`
                  : "Admin must provision the selected review process"
              }
            />
            <ChecklistItem
              ok={staffCount > 0}
              title="Required reviewers assigned"
              detail={`${staffCount} staff user${staffCount === 1 ? "" : "s"} listed`}
            />
            <ChecklistItem
              ok={projection?.read_only === false || Boolean(projection)}
              title="Organization active"
              detail="Workspace can be used for launch setup"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Review process contract</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {readinessRows.length === 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                No PM review process selection has been recorded for this organization.
              </div>
            ) : (
              readinessRows.map((row) => (
                <div
                  key={`${row.program_code}:${row.academic_year}`}
                  className="rounded-md border bg-white p-3 text-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium">
                      {row.program_code} {row.academic_year}
                    </div>
                    <Pill>{statusLabel(row.status)}</Pill>
                  </div>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    <ContractLine
                      label="Selected Review Process"
                      value={row.selected_review_process_name ?? "Not selected"}
                    />
                    <ContractLine
                      label="Provisioned Review Process"
                      value={
                        row.provisioned_workflow_config_id
                          ? `${row.provisioned_review_process_name ?? "Workflow"} #${
                              row.provisioned_workflow_config_id
                            }`
                          : "Not provisioned"
                      }
                    />
                    <ContractLine
                      label="Runtime Transitions"
                      value={`${row.transition_count}`}
                    />
                    <ContractLine
                      label="Active Form Versions"
                      value={`${row.active_form_version_count}`}
                    />
                  </div>
                  <p className={row.mismatch ? "mt-2 text-red-700" : "mt-2 text-gray-600"}>
                    {row.message}
                  </p>
                  {canProvision(row) ? (
                    <div className="mt-3">
                      <Button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          onProvisionReviewProcess(row.program_code, row.academic_year)
                        }
                      >
                        Provision Selected Review Process
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <details className="rounded-lg border bg-white">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
            Advanced Governance Notes
          </summary>
          <div className="space-y-2 border-t p-4">
            {(projection?.drift_warnings ?? []).length === 0 ? (
              <div className="text-sm text-gray-600">No advanced notes for this organization.</div>
            ) : (
              projection?.drift_warnings.map((warning) => (
                <div key={warning.warning_id} className="rounded-md border bg-gray-50 p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{warning.title}</span>
                    <Pill>{warning.severity}</Pill>
                  </div>
                  <p className="mt-1 text-gray-600">{warning.recommended_action}</p>
                </div>
              ))
            )}
          </div>
        </details>

        <Card>
          <CardHeader>
            <CardTitle>Recent changes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentChanges.length > 0 ? (
              recentChanges.slice(0, 5).map((change) => (
                <div key={change.audit_id} className="border-b pb-3 last:border-b-0 last:pb-0">
                  <div className="text-sm font-medium">{humanAction(change.action)}</div>
                  <div className="text-xs text-gray-500">
                    {change.timestamp ?? "No timestamp"} · {change.severity ?? "low"} risk
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-600">No recent changes recorded.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function canProvision(row: ReviewProcessReadinessProjection): boolean {
  return (
    row.status === "selected" &&
    Boolean(row.selected_workflow_template_id) &&
    !row.provisioned_workflow_config_id
  );
}

function statusLabel(status: string): string {
  switch (status) {
    case "not_selected":
      return "Not Selected";
    case "selected":
      return "Selected";
    case "provisioned":
      return "Provisioned";
    case "ready_for_launch":
      return formatReadinessLabel(status);
    case "mismatch":
      return "Mismatch";
    default:
      return status;
  }
}

function ContractLine({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase text-gray-500">{label}</div>
      <div className="mt-1 text-gray-950">{value}</div>
    </div>
  );
}

function ChecklistItem({
  ok,
  title,
  detail,
}: {
  ok: boolean;
  title: string;
  detail: string;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
      }`}
    >
      <div className={ok ? "font-medium text-green-900" : "font-medium text-red-900"}>
        {ok ? "✓ " : "! "}
        {title}
      </div>
      <div className={ok ? "mt-1 text-xs text-green-700" : "mt-1 text-xs text-red-700"}>
        {detail}
      </div>
    </div>
  );
}

function humanAction(action: string): string {
  return action.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
